import type {
  CashSource,
  CycleCloseReason,
  EarlySettlementRule,
  InstalmentStatus,
  InterestMethod,
  InterestRatePeriod,
  LoanAccountStatus,
  LoanCycleStatus,
  DisclosureHeadlineRate,
  LoanFeeCalculation,
  LoanFeeCollectionMode,
  LoanProductStatus,
  MonthLengthConvention,
  PenaltyCalculation,
  ProvisionBasis,
  RepaymentFrequency,
} from '@/api/loans-types'

/**
 * The words the loan screens use, and the small rules about which action a round is in a state
 * for.
 *
 * Labels are written for the person at the counter, not for the enum. "Declining balance, equal
 * instalment" is what a loan officer explains to a member; `DecliningBalanceEqualInstalment` is
 * what the wire carries, and the two are kept apart here so neither has to bend to the other.
 */

export const LOAN_ACCOUNT_STATUSES = ['Active', 'Dormant', 'Closed'] as const satisfies readonly LoanAccountStatus[]

export const LOAN_PRODUCT_STATUSES = ['Active', 'Retired'] as const satisfies readonly LoanProductStatus[]

export const CYCLE_STATUS_LABELS: Record<LoanCycleStatus, string> = {
  Submitted: 'Submitted',
  UnderAppraisal: 'Under appraisal',
  PendingApproval: 'Awaiting a countersignature',
  Approved: 'Approved',
  PendingDisbursement: 'Disbursement awaiting a countersignature',
  Active: 'Active',
  Closed: 'Closed',
  Rejected: 'Refused',
  Withdrawn: 'Withdrawn by the applicant',
  Cancelled: 'Cancelled by the branch',
  WrittenOff: 'Written off',
}

export const cycleStatusLabel = (status: LoanCycleStatus) => CYCLE_STATUS_LABELS[status] ?? status

export const INTEREST_METHOD_LABELS: Record<InterestMethod, string> = {
  Flat: 'Flat rate',
  DecliningBalanceEqualInstalment: 'Declining balance, equal instalment',
  DecliningBalanceEqualPrincipal: 'Declining balance, equal principal',
}

export const INTEREST_PERIOD_LABELS: Record<InterestRatePeriod, string> = {
  PerMonth: 'per month',
  PerAnnum: 'per year',
  PerCycle: 'per round',
}

export const MONTH_LENGTH_LABELS: Record<MonthLengthConvention, string> = {
  FourWeekMonth: 'Four-week month',
  CalendarMonth: 'Calendar month',
}

export const REPAYMENT_FREQUENCY_LABELS: Record<RepaymentFrequency, string> = {
  Weekly: 'Weekly',
  Fortnightly: 'Fortnightly',
  Monthly: 'Monthly',
}

export const FEE_CALCULATION_LABELS: Record<LoanFeeCalculation, string> = {
  FlatAmount: 'Flat amount',
  PercentOfPrincipal: 'Percentage of principal',
}

export const FEE_COLLECTION_LABELS: Record<LoanFeeCollectionMode, string> = {
  DeductFromDisbursement: 'Deducted from the disbursement',
  Capitalised: 'Added to what is owed',
}

export const PENALTY_CALCULATION_LABELS: Record<PenaltyCalculation, string> = {
  FlatPerMissedInstalment: 'Flat amount per missed instalment',
  PercentOfOverduePerWeek: 'Percentage of the overdue amount, per week',
}

export const EARLY_SETTLEMENT_LABELS: Record<EarlySettlementRule, string> = {
  FullFlatInterest: 'Full interest, no rebate',
  RebateUnearnedInterest: 'Unearned interest rebated',
}

export const PROVISION_BASIS_LABELS: Record<ProvisionBasis, string> = {
  PrincipalOutstanding: 'Principal outstanding',
  PrincipalAndInterestOutstanding: 'Principal and interest outstanding',
}

export const DISCLOSURE_HEADLINE_LABELS: Record<DisclosureHeadlineRate, string> = {
  QuotedFlat: 'Quoted flat rate',
  NominalAnnual: 'Nominal annual rate',
  EffectiveCompounded: 'Effective annual rate (EIR)',
}

export const CASH_SOURCE_LABELS: Record<CashSource, string> = {
  TellerTill: 'Counter till',
  FieldOfficerTill: 'Field officer till',
}

export const INSTALMENT_STATUS_LABELS: Record<InstalmentStatus, string> = {
  Pending: 'Pending',
  PartiallyPaid: 'Partly paid',
  Paid: 'Paid',
  Overdue: 'Overdue',
  Superseded: 'Superseded by a restructure',
  WrittenOff: 'Written off',
}

export const CLOSE_REASON_LABELS: Record<CycleCloseReason, string> = {
  FullyRepaid: 'Repaid in full',
  EarlySettled: 'Settled early',
  OffsetClosed: 'Closed by a guarantee offset',
  WrittenOff: 'Written off',
}

/**
 * The prerequisite gates, in the words a teller would use to tell a member what to bring.
 *
 * The codes come from the Savings module and are stable; anything unrecognised is shown as the
 * code itself rather than as a soothing generic sentence, because a gate nobody can name is a
 * gate nobody can clear.
 */
export const LOAN_GATE_LABELS: Record<string, string> = {
  'savings.prerequisite.no_savings_account': 'No savings account — one has to be opened first.',
  'savings.prerequisite.savings_account_not_active':
    'The savings account is not active. It is dormant, frozen, or closed.',
  'savings.prerequisite.no_security_deposit_account': 'No security deposit account on file.',
  'savings.prerequisite.security_deposit_short':
    'The security deposit does not cover this loan. A top-up is needed before it can proceed.',
  'savings.prerequisite.no_passbook': 'No usable passbook. One has to be issued, or replaced.',
}

export const gateLabel = (code: string) => LOAN_GATE_LABELS[code] ?? code

/**
 * Where an unmet gate is cleared.
 *
 * A refusal that names a requirement without saying where to satisfy it sends the operator
 * hunting through the application while a member waits at the counter — which is exactly what
 * happened the first time somebody met `no_passbook` in the wild. Every gate the Savings module
 * can raise resolves to the tab on that customer's savings record where the fix lives.
 */
export const gateDestinationTab = (code: string): 'accounts' | 'passbooks' =>
  code === 'savings.prerequisite.no_passbook' ? 'passbooks' : 'accounts'

export const gateActionLabel = (code: string) =>
  code === 'savings.prerequisite.no_passbook' ? 'Issue a passbook' : 'Open the savings record'

/**
 * The gate check itself failing, which is a different thing from a gate being unmet.
 *
 * `loans.savings_directory_unavailable` means the Savings module could not answer — the deposit
 * may well be there and nobody can see it. Reporting that as "requirements not met" would send a
 * member home for a passbook they are holding, so every screen that reads a gate checks for this
 * code first.
 */
export const SAVINGS_DIRECTORY_UNAVAILABLE = 'loans.savings_directory_unavailable'

/** The buckets a repayment settles, in the order the allocator publishes them. */
export const ALLOCATION_BUCKETS = ['Penalty', 'Fee', 'Interest', 'Principal'] as const

/* ─── What a round is in a state for ─────────────────────────────────────────────────────────
 *
 * Mirrors the domain's own guards. The server refuses anything else, so these only keep an
 * operator from opening a form that could not possibly succeed — they never grant anything.
 */

export const canRecordAppraisal = (status: LoanCycleStatus) =>
  status === 'Submitted' || status === 'UnderAppraisal'

/** Approval and refusal share a window, and both need an appraisal on file first. */
export const canDecide = (status: LoanCycleStatus) =>
  status === 'Submitted' || status === 'UnderAppraisal' || status === 'PendingApproval'

/**
 * Withdrawn before a decision, cancelled after one — the server picks which, from the state.
 * A disbursement still waiting for a countersignature can still be called off: it has moved no
 * money, and a round that could only ever be released would be a queue nobody could clear.
 */
export const canCancel = (status: LoanCycleStatus) =>
  canDecide(status) || status === 'Approved' || status === 'PendingDisbursement'

export const canDisburse = (status: LoanCycleStatus) =>
  status === 'Approved' || status === 'PendingDisbursement'

/** Collections, waivers, restructures, write-offs and offsets all need a live round. */
export const isLive = (status: LoanCycleStatus) => status === 'Active'

export const canRecordRecovery = (status: LoanCycleStatus) => status === 'WrittenOff'

/** A round nobody is waiting on: closed, refused, withdrawn, cancelled. */
export const isSettled = (status: LoanCycleStatus) =>
  status === 'Closed' || status === 'Rejected' || status === 'Withdrawn' || status === 'Cancelled'

/**
 * A rate as it is written on a loan agreement.
 *
 * Rates cross the wire as fractions in 0–1, which is the one numeric convention on these screens
 * that is not minor units. 0.035 is 3.5%.
 */
export function formatRate(fraction: number, fractionDigits = 2): string {
  return `${(fraction * 100).toFixed(fractionDigits)}%`
}

/**
 * Percentages are keyed as percentages and stored as fractions. These two are that boundary.
 *
 * The division is rounded to six places on the way in because `2.9 / 100` is
 * 0.028999999999999998 in binary floating point, and a rate is a decimal on the server. Six places
 * is finer than any rate anybody writes on a loan agreement and coarse enough to land on the
 * number that was typed. Rates are the one figure on these screens that is not minor units — money
 * never goes near this.
 */
export const percentToFraction = (percent: number) => Number((percent / 100).toFixed(6))

export const fractionToPercent = (fraction: number) => Number((fraction * 100).toFixed(4))

/**
 * Reads a keyed percentage. `NaN` for anything that is not one, including an empty box.
 *
 * `Number()` rather than a float parse, because a float parse is banned in this codebase and the
 * ban is right: it is how a money field ends up going through a locale-sensitive read. This is a
 * rate, not money, and it still goes through one deliberate door.
 */
export function parsePercent(text: string): number {
  const trimmed = text.trim()
  return trimmed === '' ? Number.NaN : Number(trimmed)
}

/** Today, in the ISO form every date member on the wire uses. Display only — never a value date. */
export const todayIso = () => new Date().toISOString().slice(0, 10)

/**
 * An idempotency key for a posting.
 *
 * The server settles a repayment or a settlement once per key and answers a replay with the
 * original result, which is what makes a double click, a retried request, or a browser that
 * resent the form harmless. A fresh key is minted per posting attempt the operator *intends* —
 * never per render, and never per retry of the same intent.
 *
 * The contract types this member as a UUID and the server parses it as one, so every branch here
 * produces an RFC 4122 v4 string. `crypto.randomUUID` is only defined in a secure context, which
 * a branch served over plain HTTP is not — a key shaped like anything else would be refused as
 * malformed at exactly the moment the member has already handed over the cash.
 */
export const newClientOperationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  // Version 4, variant 1 — the two fields a parser checks.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
